import { faCheck, faStar } from '@fortawesome/free-solid-svg-icons';
import { FormToggle, FormToggleSize } from '@tryhackme/thm-ui-components';
import { useEffect, useState } from 'react';
import { StandardActionModal } from 'src/common/components/standard-action-modal';
import { formatPriceSimple } from 'src/common/helpers/subscription';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { useTrackCTAClickMutation, useTrackEventMutation } from 'src/common/slices';
import { anonymousId } from 'src/common/tracking/data';
import { PREMIUM_FEATURES } from './go-premium-modal.constants';
import { useGoPremiumModalData } from './go-premium-modal.hooks';
import * as S from './go-premium-modal.styles';
import type { GoPremiumModalData, GoPremiumModalProps } from './go-premium-modal.types';
export const GoPremiumModal = ({
  open,
  onOpenChange
}: GoPremiumModalProps) => {
  const [isAnnualBilling, setIsAnnualBilling] = useState(true);
  const modalData = useGoPremiumModalData();
  const [trackEvent] = useTrackEventMutation();
  const [trackCTAClick] = useTrackCTAClickMutation();
  const trackButtonClick = (button_text: string) => trackCTAClick({
    button_text,
    page: 'Room - Go Premium Attackbox Modal',
    anonymousId
  });
  const toggleBillingCycle = () => {
    setIsAnnualBilling(prev => !prev);
  };
  useEffect(() => {
    if (open) {
      trackEvent({
        event: 'view-popup',
        properties: {
          name: 'go-premium-attackbox-modal'
        }
      });
    }
  }, [open, trackEvent]);
  const handleSubscribe = () => {
    trackButtonClick('Subscribe now');
    if (!modalData) return;
    modalData.onSubscribe(isAnnualBilling ? 'annual' : 'monthly');
  };
  const handleMaybeLater = () => {
    trackButtonClick('Maybe later');
    onOpenChange(false);
  };
  return <StandardActionModal modalTitleText="Get unlimited AttackBox time!" modalBodyText={<BillingToggle isAnnualBilling={isAnnualBilling} onToggle={toggleBillingCycle} modalData={modalData} />} dismissButtonTitle="Maybe later" showIcon={false} showActionButton open={open} onOpenChange={onOpenChange} actionButtonTitle="Subscribe now" actionButtonColor="primary" handleModalAction={handleSubscribe} actionLoading={!modalData} handleCloseButtonAction={handleMaybeLater} data-sentry-element="StandardActionModal" data-sentry-component="GoPremiumModal" data-sentry-source-file="go-premium-modal.tsx">
      <PricingCard isAnnualBilling={isAnnualBilling} modalData={modalData} data-sentry-element="PricingCard" data-sentry-source-file="go-premium-modal.tsx" />
    </StandardActionModal>;
};
interface BillingToggleProps {
  isAnnualBilling: boolean;
  onToggle: () => void;
  modalData: GoPremiumModalData | null;
}
const BillingToggle = ({
  isAnnualBilling,
  onToggle,
  modalData
}: BillingToggleProps) => {
  if (!modalData) return null;
  return <S.BillingToggleContainer data-sentry-element="unknown" data-sentry-component="BillingToggle" data-sentry-source-file="go-premium-modal.tsx">
      <S.BillingText $active={!isAnnualBilling} data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">Monthly</S.BillingText>
      <FormToggle checked={isAnnualBilling} onCheckedChange={onToggle} size={FormToggleSize.MEDIUM} aria-label="Toggle billing period" data-sentry-element="FormToggle" data-sentry-source-file="go-premium-modal.tsx" />
      <S.BillingText $active={isAnnualBilling} data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">Yearly</S.BillingText>
      {!isAnnualBilling && <S.YearlySavingsTag>{`Get ${modalData.monthsSaved} months free with yearly`}</S.YearlySavingsTag>}
    </S.BillingToggleContainer>;
};
interface PricingCardProps {
  isAnnualBilling: boolean;
  modalData: GoPremiumModalData | null;
}
const PricingCard = ({
  isAnnualBilling,
  modalData
}: PricingCardProps) => {
  const isDarkMode = useIsDarkMode();
  if (!modalData) {
    return <S.LoadingCard />;
  }
  const {
    monthlyPrice,
    yearlyPrice,
    yearlyTotal,
    currency,
    originalYearlyPrice,
    isDiscounted,
    monthsSaved,
    campaignDiscount,
    isStudentDiscount
  } = modalData;
  const currentPrice = formatPriceSimple(isAnnualBilling ? yearlyPrice : monthlyPrice);
  const billingText = isAnnualBilling ? `Billed annually (${currency}${formatPriceSimple(yearlyTotal)}/year)` : 'Billed monthly';
  const priceParts = currentPrice.split('.');
  const priceInteger = priceParts[0];
  const priceDecimal = priceParts[1];
  const originalPrice = isAnnualBilling && originalYearlyPrice ? formatPriceSimple(originalYearlyPrice) : undefined;
  const getBannerText = () => {
    if (!isDiscounted) {
      return `Get ${monthsSaved} months free with yearly`;
    }
    if (isStudentDiscount) {
      return `Students save more — get up to ${monthsSaved} months free`;
    }
    return `Save ${campaignDiscount}% on discount code`;
  };
  return <S.PricingCardContainer $withYearlyBanner={isAnnualBilling} data-sentry-element="unknown" data-sentry-component="PricingCard" data-sentry-source-file="go-premium-modal.tsx">
      {isAnnualBilling && <S.YearlyBanner $isDarkMode={isDarkMode}>
          <S.StarIcon icon={faStar} />
          <S.BannerText>{getBannerText()}</S.BannerText>
        </S.YearlyBanner>}

      <S.PricingContent $isDarkMode={isDarkMode} data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
        <S.PriceSection data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
          <S.PriceRow data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
            {originalPrice && <S.OriginalPrice>
                {currency}
                {originalPrice}
              </S.OriginalPrice>}
            <S.PriceContainer data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
              <S.CurrencySign data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">{currency}</S.CurrencySign>
              <S.PriceInteger data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">{priceInteger}</S.PriceInteger>
              {priceDecimal && priceDecimal !== '00' && <S.PriceDecimal>
                  <S.PriceDecimalText>.{priceDecimal}</S.PriceDecimalText>
                </S.PriceDecimal>}
            </S.PriceContainer>
            <S.PerMonthText data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">per month</S.PerMonthText>
          </S.PriceRow>
          <S.BillingTextContainer data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
            <S.BillingDescription data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">{billingText}</S.BillingDescription>
          </S.BillingTextContainer>
        </S.PriceSection>

        <S.FeaturesSection data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
          <S.FeaturesList data-sentry-element="unknown" data-sentry-source-file="go-premium-modal.tsx">
            {PREMIUM_FEATURES.map(feature => <S.FeatureItem key={feature}>
                <S.CheckIcon icon={faCheck} />
                <S.FeatureText>{feature}</S.FeatureText>
              </S.FeatureItem>)}
          </S.FeaturesList>
        </S.FeaturesSection>
      </S.PricingContent>
    </S.PricingCardContainer>;
};